import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/db/useAuth';
import { myRolesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { Loader2 } from 'lucide-react';
import type { FC, PropsWithChildren } from 'react';

/**
 * Gates the admin UI on the signed-in user's `user-roles` doc (self-readable per rules) — the sole
 * authority on admin access, shared with firestore.rules, storage.rules and the callables'
 * `requireAdmin()`. {@link RequireLogin} only establishes WHO you are.
 *
 * Without this, anyone who can sign in with Google passes the login gate and every Firestore read
 * fails permission-denied — surfacing as broken pages instead of a clear "you need access" message.
 */
const RequireRole: FC<PropsWithChildren> = ({ children }) => {
    const { user, logout } = useAuth();
    const [roles, rolesLoadable] = useLoadableAtomValue(myRolesState);

    if (rolesLoadable.state === 'loading') {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-100">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const hasReadAccess = roles?.admin === true || roles?.['read-admin'] === true;
    if (rolesLoadable.state === 'hasError' || !hasReadAccess) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-100 p-4">
                <Card className="w-[400px] max-w-full shadow-lg">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-semibold">Access Needed</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center gap-4 text-center">
                        <div className="section-description">
                            {user?.email ?? 'Your account'} is signed in but doesn't have an admin role yet. Ask an
                            admin to grant access from the Users page, then reload.
                        </div>
                        <Button variant="outline" onClick={logout}>
                            Sign Out
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return children;
};

export default RequireRole;
